import React, { useState } from 'react';
import { Box, Typography, Paper, Grid, Avatar, Button, TextField, IconButton, Snackbar, Alert, useTheme } from '@mui/material';
import { 
    AccountCircle, Email, Phone, VpnKey, Edit, Save, Close, CloudUpload, CameraAlt
} from '@mui/icons-material';

const AdminProfile = () => {
    const theme = useTheme();
    const [isEditing, setIsEditing] = useState(false);
    const [profileData, setProfileData] = useState({
        fullName: 'Super System Admin',
        email: 'admin@yourhrms.com',
        phone: '9876543210',
        role: 'Global Administrator',
        lastLogin: '2025-10-03 11:20 AM',
    });
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        // Simulate API call to save data
        setIsEditing(false);
        setSnackbarOpen(true);
        console.log('Profile Saved:', profileData);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const inputStyle = {
        '& .MuiInputBase-input': {
            transition: 'border 0.3s ease-in-out',
            borderBottom: isEditing ? `2px solid ${theme.palette.secondary.main}` : '1px solid transparent',
        },
        '&:hover .MuiInputBase-input': {
            borderBottom: '1px solid #999',
        }
    };

    return (
        <Box sx={{ animation: 'fadeIn 0.5s ease-out' }}>
            <Typography variant="h4" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                My Admin Profile 🔑
            </Typography>

            <Paper sx={{ p: 4, boxShadow: 6, borderRadius: 2, bgcolor: 'white' }}>
                <Grid container spacing={4}>
                    
                    {/* LEFT COLUMN: Avatar and Status */}
                    <Grid item xs={12} md={4} sx={{ textAlign: 'center', borderRight: { md: '1px solid #eee' } }}>
                        <Avatar 
                            sx={{ 
                                width: 120, 
                                height: 120, 
                                margin: '0 auto 16px', 
                                bgcolor: theme.palette.secondary.main, 
                                fontSize: '3rem',
                                boxShadow: 3
                            }}
                        >
                            {profileData.fullName.charAt(0)}
                        </Avatar>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.primary.dark }}>
                            {profileData.fullName}
                        </Typography>
                        <Typography color="success.main" sx={{ mt: 1, fontWeight: 600 }}>
                            Account Status: Active
                        </Typography>

                        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button 
                                variant="outlined" 
                                color="primary" 
                                startIcon={<CloudUpload />}
                            >
                                Upload New Photo
                            </Button>
                            <Button 
                                variant="outlined" 
                                color="primary" 
                                startIcon={<CameraAlt />}
                            >
                                Take Photo Now
                            </Button>
                        </Box>
                    </Grid>

                    {/* RIGHT COLUMN: Profile Details */}
                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6" sx={{ borderLeft: `3px solid ${theme.palette.secondary.main}`, pl: 1, fontWeight: 600 }}>
                                Account Details
                            </Typography>
                            <IconButton 
                                color={isEditing ? 'error' : 'primary'} 
                                onClick={() => setIsEditing(!isEditing)}
                                size="large"
                            >
                                {isEditing ? <Close /> : <Edit />}
                            </IconButton>
                        </Box>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Full Name"
                                    name="fullName"
                                    value={profileData.fullName}
                                    onChange={handleChange}
                                    InputProps={{ readOnly: !isEditing, startAdornment: <AccountCircle sx={{ mr: 1, color: '#999' }} /> }}
                                    fullWidth
                                    variant="standard"
                                    sx={inputStyle}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Email Address"
                                    name="email"
                                    value={profileData.email}
                                    onChange={handleChange}
                                    InputProps={{ readOnly: !isEditing, startAdornment: <Email sx={{ mr: 1, color: '#999' }} /> }}
                                    fullWidth
                                    variant="standard"
                                    sx={inputStyle}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Phone Number"
                                    name="phone"
                                    value={profileData.phone}
                                    onChange={handleChange}
                                    InputProps={{ readOnly: !isEditing, startAdornment: <Phone sx={{ mr: 1, color: '#999' }} /> }}
                                    fullWidth
                                    variant="standard"
                                    sx={inputStyle}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Role"
                                    name="role"
                                    value={profileData.role}
                                    InputProps={{ readOnly: true, startAdornment: <VpnKey sx={{ mr: 1, color: '#999' }} /> }}
                                    fullWidth
                                    variant="standard"
                                    sx={inputStyle}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                    **Last Activity:** {profileData.lastLogin} (Admin Login)
                                </Typography>
                            </Grid>
                        </Grid>
                        
                        {isEditing && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<Save />}
                                onClick={handleSave}
                                sx={{ mt: 4, float: 'right', animation: 'slideIn 0.5s ease-out' }}
                            >
                                Save Changes
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {/* Success Notification */}
            <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                    Profile details updated successfully!
                </Alert>
            </Snackbar>
            
            {/* Custom CSS for animations (place this in a global style file or index.html) */}
            <style jsx global>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slideIn {
                    from { opacity: 0; transform: translateX(20px); }
                    to { opacity: 1; transform: translateX(0); }
                }
            `}</style>
        </Box>
    );
};

export default AdminProfile;